import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ROOT_URL from "../../../Utilities/apiconfig";
import { Helmet } from 'react-helmet';

const disabledDoctors = [
  "Dr S Arivazhagan",
  "Dr. Munireddy M V",
  "Dr Ramanjanayalu",
  "Dr Anagha Auradkar",
  "Dr. Chandra Shekar M",
  "Dr Volga Joshy (PT)",
  "Dr Swetha",
  "Dr Mahesh",
];

const BookAppointment = () => {
  const [formData, setFormData] = useState({
    departmentName: '',
    doctorName: '',
    patientName: '',
    phone: '',
    email: '',
    date: '',
    timeSlot: '',
    message: '',
  });

  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);

  // blocked dates + flag
  const [doctorBlockedDates, setDoctorBlockedDates] = useState([]);
  const [isDateBlocked, setIsDateBlocked] = useState(false);
  const [isSunday, setIsSunday] = useState(false);

  const isSlotPassed = (slotStr) => {
    if (!formData.date) return false;
    const today = new Date().toISOString().split("T")[0];
    if (formData.date !== today) return false;
    const match = slotStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return false;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    const now = new Date();
    const slotMinutes = hours * 60 + minutes;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return slotMinutes <= nowMinutes;
  };

  const navigate = useNavigate();

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${ROOT_URL}/api/departments`);
        setDepartments(response.data);
      } catch (err) {
        console.error('Failed to fetch departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch doctors when department changes
  useEffect(() => {
    const fetchDoctors = async () => {
      if (formData.departmentName) {
        try {
          const response = await axios.get(`${ROOT_URL}/api/medicos`);
          const filteredDoctors = response.data.filter(
            (doc) => doc.department === formData.departmentName
          );
          setDoctors(filteredDoctors);
        } catch (err) {
          console.error('Failed to fetch doctors:', err);
        }
      } else {
        setDoctors([]);
        setTimeSlots([]);
        setBookedSlots([]);
        setDoctorBlockedDates([]);
        setIsDateBlocked(false);
      }
    };
    fetchDoctors();
  }, [formData.departmentName]);

  // Fetch time slots + blocked dates when doctor changes
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (formData.doctorName) {
        try {
          const response = await axios.get(`${ROOT_URL}/api/medicos`);
          const doctorData = response.data.find(
            (doc) => doc.name === formData.doctorName
          );

          setTimeSlots(doctorData ? doctorData.timeSlots || [] : []);
          setDoctorBlockedDates(doctorData ? doctorData.blockedDates || [] : []);

          // Reset date/time when doctor changes
          setFormData((prev) => ({ ...prev, date: '', timeSlot: '' }));
          setBookedSlots([]);
          setIsDateBlocked(false);
        } catch (err) {
          console.error('Failed to fetch time slots:', err);
        }
      } else {
        setTimeSlots([]);
        setBookedSlots([]);
        setDoctorBlockedDates([]);
        setIsDateBlocked(false);
      }
    };
    fetchTimeSlots();
  }, [formData.doctorName]);

  // Check if selected date is blocked
  useEffect(() => {
    if (formData.date && doctorBlockedDates.length > 0) {
      const blocked = doctorBlockedDates.includes(formData.date);
      setIsDateBlocked(blocked);

      if (blocked) {
        setBookedSlots([]);
        setFormData((prev) => ({ ...prev, timeSlot: '' }));
      }
    } else {
      setIsDateBlocked(false);
    }
  }, [formData.date, doctorBlockedDates]);

  // Fetch booked slots for selected doctor + date (only if date not blocked)
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (formData.doctorName && formData.date && !isDateBlocked) {
        try {
          const response = await axios.get(`${ROOT_URL}/api/appointments/all`);
          const appointments = response.data.filter(
            (appt) =>
              appt.doctorName === formData.doctorName &&
              appt.date === formData.date
          );
          const slots = appointments.flatMap((appt) => appt.timeSlots || []);
          setBookedSlots(slots);
        } catch (err) {
          console.error('Failed to fetch booked slots:', err);
        }
      } else {
        setBookedSlots([]);
      }
    };

    fetchBookedSlots();
  }, [formData.doctorName, formData.date, isDateBlocked]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const day = new Date(value).getDay();
    if (day === 0) {
      setIsSunday(true);
      setFormData((prev) => ({ ...prev, date: "" }));
    } else {
      setIsSunday(false);
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDateBlocked) {
      toast.error('No doctor slots available on the selected date.');
      return;
    }

    if (isSunday) {
      toast.error('Appointments are not available on Sundays. Please select another date.');
      return;
    }

    if (!formData.timeSlot) {
      toast.error('Please select a time slot.');
      return;
    }

    if (isSlotPassed(formData.timeSlot)) {
      toast.error('Slot not available. Please select another time slot.');
      return;
    }

    if (disabledDoctors.includes(formData.doctorName)) {
      toast.error('No doctor slots available.');
      return;
    }

    try {
      const payload = {
        ...formData,
        timeSlots: [formData.timeSlot],
      };

      await axios.post(`${ROOT_URL}/api/appointments/add`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      toast.success(
        "We've received your request. You will receive a call shortly to confirm your appointment."
      );

      setFormData({
        departmentName: '',
        doctorName: '',
        patientName: '',
        phone: '',
        email: '',
        date: '',
        timeSlot: '',
        message: '',
      });
      setTimeSlots([]);
      setBookedSlots([]);
      setDoctorBlockedDates([]);
      setIsDateBlocked(false);
    } catch (error) {
      console.error('Error adding appointment:', error);
      toast.error(
        error.response?.data?.message ||
          'Failed to add appointment. Please try again.'
      );
    }
  };

  return (
    <div className="container mt-4 mb-4">
      <Helmet>
        <title>Book Appointment | Columbiaa Hospital, Kasturi Nagar, Bangalore</title>
        <meta name="description" content="Book an appointment at Columbiaa Hospital, Kasturi Nagar, Bangalore. Easy online booking with our expert doctors across all specialities." />
        <meta name="keywords" content="book appointment, Columbiaa Hospital, online appointment, doctor booking, Kasturi Nagar, Bangalore" />
      </Helmet>

      <h2 className="mb-4" aria-label="Book Appointment">Book Appointment</h2>
      <p className="mb-4" aria-label="Book your consultation with our expert doctors">
        Book your consultation with our expert doctors at Columbiaa Hospital, Kasturi Nagar, Bangalore
      </p>
      
      <form onSubmit={handleSubmit} className="border p-5 rounded shadow" aria-label="Appointment booking form">
        <div className="mb-3">
          <label htmlFor="departmentName" className="form-label">Department Name</label>
          <select
            id="departmentName"
            className="form-control"
            name="departmentName"
            value={formData.departmentName}
            onChange={handleChange}
            required
            aria-label="Select department"
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept._id} value={dept.departmentName}>
                {dept.departmentName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="doctorName" className="form-label">Doctor Name</label>
          <select
            id="doctorName"
            className="form-control"
            name="doctorName"
            value={formData.doctorName}
            onChange={handleChange}
            required
            aria-label="Select doctor"
          >
            <option value="">Select Doctor</option>
            {doctors.map((doc) => {
              const isDisabled = disabledDoctors.includes(doc.name);
              return (
                <option key={doc._id} value={doc.name} disabled={isDisabled}>
                   {doc.name}{isDisabled ? " (No Slots Available)" : ""}
                </option>
              );
            })}
          </select>
          {disabledDoctors.includes(formData.doctorName) && (
            <p className="text-danger mt-1" role="alert">
              No doctor slots available.
            </p>
          )}
        </div>

        {/* Patient Name */}
        <div className="mb-3">
          <label htmlFor="patientName" className="form-label">Patient Name</label>
          <input
            id="patientName"
            type="text"
            className="form-control"
            name="patientName"
            value={formData.patientName}
            onChange={handleChange}
            placeholder="Enter patient name"
            required
            aria-label="Enter patient name"
          />
        </div>

        {/* Phone */}
        <div className="mb-3">
          <label htmlFor="phone" className="form-label">Phone</label>
          <input
            id="phone"
            type="tel"
            className="form-control"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter phone number"
            required
            aria-label="Enter phone number"
          />
        </div>

        {/* Email */}
        <div className="mb-3">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            id="email"
            type="email"
            className="form-control"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
            aria-label="Enter email address"
          />
        </div>

        {/* Message */}
        <div className="mb-3">
          <label htmlFor="message" className="form-label">Message</label>
          <textarea
            id="message"
            className="form-control"
            name="message"
            rows="3"
            value={formData.message}
            onChange={handleChange}
            placeholder="Reason for visit / message"
            aria-label="Enter reason for visit or message"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="date" className="form-label">Date</label>
          <input
            id="date"
            type="date"
            className="form-control"
            name="date"
            value={formData.date}
             onChange={handleDateChange}
             required
             aria-label="Select appointment date"
           />
           {isDateBlocked && (
             <p className="text-danger mt-1" role="alert">
No slots available on this date. Please choose another date.            </p>
           )}
           {isSunday && (
             <p className="text-danger mt-1" role="alert">
               Appointments are not available on Sundays. Please select another date.
             </p>
           )}
         </div>

        <div className="mb-3">
          <label htmlFor="timeSlot" className="form-label">Time Slot</label>
          <select
            id="timeSlot"
            className="form-control"
            name="timeSlot"
            value={formData.timeSlot}
            onChange={handleChange}
            required
            aria-label="Select appointment time slot"
            disabled={isDateBlocked || timeSlots.length === 0}
          >
            <option value="">
              {isDateBlocked
                ? 'No doctor slots available'
                : 'Select Time Slot'}
            </option>
            {!isDateBlocked &&
              timeSlots.map((slot, index) => {
                const passed = isSlotPassed(slot);
                const isBooked = bookedSlots.includes(slot);
                return (
                  <option
                    key={index}
                    value={slot}
                    disabled={isBooked || passed}
                    style={{
                      color: isBooked ? 'gray' : passed ? 'gray' : 'black',
                    }}
                  >
                    {slot} {isBooked ? '(Booked)' : passed ? '(Slot Not Available)' : ''}
                  </option>
                );
              })}
          </select>
        </div>

        <button type="submit" className="btn btn-primary" aria-label="Submit appointment request">
          Submit
        </button>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={20000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </div>
  );
};

export default BookAppointment;
